import { useEffect, useRef, useState } from "react"
import { useJsApiLoader } from "@react-google-maps/api"
import { Input } from "antd"
import { MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/outline"
import { REACT_APP_GOOGLE_MAP_KEY } from "utils/constants"
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete"

const GOOGLE_KEY = REACT_APP_GOOGLE_MAP_KEY
const LIBRARIES: ["places"] = ["places"]

type GeoPickerProps = {
  onSelect?: (lat: number, lng: number) => void
  value?: string
  onChange?: (address: string) => void
  onPlaceDetails?: (place: any) => void
}

//  Inner component — only rendered after Maps API is loaded
const GeoPickerInner = ({
  onSelect,
  value,
  onChange,
  onPlaceDetails,
}: GeoPickerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isLocating, setIsLocating] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const geocoderRef = useRef<google.maps.Geocoder | null>(null)

  const {
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({ debounce: 300 })

  useEffect(() => {
    if (window.google) {
      geocoderRef.current = new google.maps.Geocoder()
    }
  }, [])

  useEffect(() => {
    setValue(value ?? "", false)
  }, [value])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
        clearSuggestions()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    onChange?.(e.target.value)
    setIsOpen(true)
  }

  const handleSelect = async (description: string) => {
    setValue(description, false)
    onChange?.(description)
    clearSuggestions()
    setIsOpen(false)
    try {
      const results = await getGeocode({ address: description })
      const { lat, lng } = await getLatLng(results[0])
      onSelect?.(lat, lng)
      onPlaceDetails?.(results[0])
    } catch (err) {
      console.error("Geocode error:", err)
    }
  }

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return
    setIsLocating(true)
    setIsOpen(false)
    clearSuggestions()

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords
        geocoderRef.current?.geocode(
          { location: { lat, lng } },
          (results, geocodeStatus) => {
            setIsLocating(false)
            if (
              geocodeStatus !== google.maps.GeocoderStatus.OK ||
              !results?.[0]
            )
              return
            const place = results[0]
            const address = place.formatted_address ?? ""
            setValue(address, false)
            onChange?.(address)
            onSelect?.(lat, lng)
            onPlaceDetails?.(place)
          }
        )
      },
      () => setIsLocating(false)
    )
  }

  const hasSuggestions = status === "OK" && data.length > 0

  return (
    <div
      ref={containerRef}
      className="relative"
    >
      <Input
        value={isLocating ? "Detecting your location..." : value}
        placeholder="Enter location address"
        prefix={<MagnifyingGlassIcon className="w-4 h-4 text-[#94A3B8]" />}
        style={{ height: "40px" }}
        onChange={handleInput}
        onFocus={() => setIsOpen(true)}
        autoComplete="off"
        disabled={isLocating}
      />

      {isOpen && (
        <ul className="absolute z-50 w-full mt-1 bg-white border-2 border-[#E2E8F0] rounded-md shadow-md overflow-hidden">
          {hasSuggestions &&
            data.map(({ place_id, description }) => {
              const isSelected = description === value
              return (
                <li
                  key={place_id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#F8FAFC] transition-colors border-b border-[#F1F5F9]"
                  onMouseDown={() => handleSelect(description)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <MapPinIcon className="w-4 h-4 shrink-0 text-[#020618]" />
                    <span className="text-sm font-normal text-[#0F172B] truncate">
                      {description}
                    </span>
                  </div>
                  <span
                    className={`shrink-0 w-4 h-4 rounded-full border flex items-center justify-center ${
                      isSelected ? "border-[#004AF5]" : "border-[#CAD5E2]"
                    }`}
                  >
                    {isSelected && (
                      <span className="w-2 h-2 rounded-full bg-[#004AF5]" />
                    )}
                  </span>
                </li>
              )
            })}

          <li
            onMouseDown={handleUseCurrentLocation}
            className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#EFF6FF] transition-colors border-t border-[#E2E8F0]"
          >
            <img
              className="w-4 h-4 shrink-0"
              src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/9f38623e-9496-4698-b135-64fd3d77e00c.png"
              alt="current location"
            />
            <span className="text-sm font-normal text-[#0F172B]">
              Use my current location
            </span>
          </li>
        </ul>
      )}
    </div>
  )
}

//  Outer component — handles loading, renders inner only when ready
const GeoPicker = (props: GeoPickerProps) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
  })

  if (!isLoaded) {
    return (
      <Input
        placeholder="Loading map..."
        prefix={<MagnifyingGlassIcon className="w-4 h-4 text-[#94A3B8]" />}
        style={{ height: "40px" }}
        disabled
      />
    )
  }

  return <GeoPickerInner {...props} />
}

export default GeoPicker

export const SILVER_MAP_STYLES = [
  { element: "geometry", rules: "color:0xf5f5f5" },
  { element: "labels.icon", rules: "visibility:off" },
  { element: "labels.text.fill", rules: "color:0x616161" },
  { element: "labels.text.stroke", rules: "color:0xf5f5f5" },
  {
    feature: "administrative.land_parcel",
    element: "labels.text.fill",
    rules: "color:0xbdbdbd",
  },
  { feature: "poi", element: "geometry", rules: "color:0xeeeeee" },
  { feature: "poi", element: "labels.text.fill", rules: "color:0x757575" },
  { feature: "poi.park", element: "geometry", rules: "color:0xe5e5e5" },
  { feature: "poi.park", element: "labels.text.fill", rules: "color:0x9e9e9e" },
  { feature: "road", element: "geometry", rules: "color:0xffffff" },
  {
    feature: "road.arterial",
    element: "labels.text.fill",
    rules: "color:0x757575",
  },
  { feature: "road.highway", element: "geometry", rules: "color:0xdadada" },
  {
    feature: "road.highway",
    element: "labels.text.fill",
    rules: "color:0x616161",
  },
  {
    feature: "road.local",
    element: "labels.text.fill",
    rules: "color:0x9e9e9e",
  },
  { feature: "transit.line", element: "geometry", rules: "color:0xe5e5e5" },
  { feature: "transit.station", element: "geometry", rules: "color:0xeeeeee" },
  { feature: "water", element: "geometry", rules: "color:0xc9c9c9" },
  { feature: "water", element: "labels.text.fill", rules: "color:0x9e9e9e" },
] as const

function mapStylesToQuery(
  styles: readonly { feature?: string; element?: string; rules: string }[]
) {
  // Convert each style entry to a `style=` parameter for the Static Maps API.
  // Example: style=feature:road|element:geometry|color:0xffffff
  return styles
    .map((s) => {
      const parts: string[] = []
      if (s.feature) parts.push(`feature:${s.feature}`)
      if (s.element) parts.push(`element:${s.element}`)
      if (s.rules) parts.push(s.rules)
      return `style=${encodeURIComponent(parts.join("|"))}`
    })
    .join("&")
}

export function getStaticMapUrl(
  lat: number,
  lng: number,
  zoom: number = 15,
  size: string = "500x120"
): string {
  // Default zoom set to 15 for a closer, more detailed view.
  const base = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=${zoom}&size=${size}&scale=2&key=${GOOGLE_KEY}`
  const stylesQuery = mapStylesToQuery(SILVER_MAP_STYLES as any)
  return `${base}&${stylesQuery}`
}
